import { useRef, useState } from 'react'
import { Loader2 } from 'lucide-react'
import { Button } from '../ui/button'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog'
import TurnstileField from '../TurnstileField'
import { useAuth } from '../../contexts/AuthContext'
import { useNotification } from '../../contexts/NotificationContext'
import { isTurnstileConfigured } from '../../services/siteEmailService'

// Ticket c492ca99: let an admin re-send the two auth emails a user can get
// stranded without.
//
//   confirmation — an account that never confirmed its email can't log in at
//                  all; Supabase rejects every attempt with "Email not
//                  confirmed", and before this the signup email was the only
//                  one the app ever sent.
//   reset        — the same dead end when a password reset email is lost.
//
// A dialog rather than a plain notify.confirm because Supabase Auth → Bot
// Protection guards both /resend and /recover, so these calls need a Turnstile
// token and the widget has to live somewhere.
const MODES = {
  confirmation: {
    title: 'Resend confirmation email',
    button: 'Send email',
    describe: (name) => `${name} never confirmed their email address, so they can't log in yet. Send the confirmation link again to`,
    sent: (email) => `Confirmation email sent to ${email}`,
    failed: 'Failed to send confirmation email',
  },
  reset: {
    title: 'Send password reset email',
    button: 'Send email',
    describe: (name) => `Send ${name} a link to choose a new password. It goes to`,
    sent: (email) => `Password reset email sent to ${email}`,
    failed: 'Failed to send password reset email',
  },
}

export default function AdminAuthEmailDialog({ user, mode, onClose }) {
  const { resendConfirmationEmail, resetPassword } = useAuth()
  const notify = useNotification()
  const turnstileRef = useRef(null)
  const [captchaToken, setCaptchaToken] = useState('')
  const [sending, setSending] = useState(false)

  const copy = MODES[mode]
  const needsCaptcha = isTurnstileConfigured()

  const handleSend = async () => {
    setSending(true)
    try {
      const send = mode === 'reset' ? resetPassword : resendConfirmationEmail
      await send(user.email, { captchaToken })
      notify.success(copy.sent(user.email))
      onClose()
    } catch (err) {
      // Supabase's per-email cooldown and project email quota both surface
      // here ("you can only request this after 41 seconds"), and that wait is
      // exactly what the admin needs to know.
      notify.error(err.message || copy.failed)
      // Tokens are single-use — clear and remount so a retry isn't rejected
      // for a stale one.
      setCaptchaToken('')
      try { turnstileRef.current?.reset() } catch { /* widget not mounted */ }
    } finally {
      setSending(false)
    }
  }

  return (
    <Dialog open onOpenChange={(open) => { if (!open) onClose() }}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{copy.title}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <p className="text-sm text-muted-foreground">
            {copy.describe(user.full_name || user.email)}{' '}
            <span className="font-medium text-foreground">{user.email}</span>.
          </p>

          <TurnstileField ref={turnstileRef} onToken={setCaptchaToken} />

          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={onClose} disabled={sending}>
              Cancel
            </Button>
            <Button
              variant="primaryNeon"
              onClick={handleSend}
              disabled={sending || (needsCaptcha && !captchaToken)}
            >
              {sending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {sending ? 'Sending...' : copy.button}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
